/**
 * Test Generation Executor
 *
 * Handles execution for both test generation nodes:
 * - generate-test-stubs: Quick scaffolding with TODOs (no AI)
 * - generate-smart-tests: AI-powered meaningful tests
 *
 * API Flow:
 * 1. POST /test-generation/stubs or /test-generation/smart → returns job_id
 * 2. GET /test-generation/status/{job_id} → check status
 * 3. GET /test-generation/results/{job_id} → get results
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the test stub generation node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and test details
 */
export async function executeStubs(config, context, onProgress = () => {}, signal = null) {
  return executeTestGeneration('stubs', config, context, onProgress, signal)
}

/**
 * Execute the smart test generation node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and test details
 */
export async function executeSmart(config, context, onProgress = () => {}, signal = null) {
  return executeTestGeneration('smart', config, context, onProgress, signal)
}

/**
 * Common execution logic for both test generation types
 */
async function executeTestGeneration(testType, config, context, onProgress, signal) {
  const apiBaseUrl = context.api_base_url
  const endpoint = testType === 'stubs' ? '/test-generation/stubs' : '/test-generation/smart'
  const typeName = testType === 'stubs' ? 'Test Stubs' : 'Smart Tests'

  onProgress(0, `Preparing ${typeName}...`)

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    source: config.source || 'refactor',
    options: buildOptions(testType, config),
  }

  onProgress(5, `Submitting ${typeName} job...`)

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || `${typeName} generation failed`)
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, `${typeName} complete`)
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, `Generating ${typeName}...`)
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-90%
        const mappedProgress = 15 + (progress * 0.75)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  // 5. Get results (optional, fetched after job succeeds)
  onProgress(92, 'Retrieving test results...')
  const results = await getResults(apiBaseUrl, jobId, signal)

  onProgress(100, `${typeName} complete`)

  // 6. Return formatted result with job data and results
  return formatResult(jobId, jobResponse, results, testType)
}

/**
 * Build options based on test type
 */
function buildOptions(testType, config) {
  if (testType === 'stubs') {
    return {
      framework: config.framework || 'junit5',
      include_mocks: config.include_mocks !== false,
      fail_on_todo: config.fail_on_todo !== false,
    }
  } else {
    return {
      framework: config.framework || 'junit5',
      mock_framework: config.mock_framework || 'mockito',
      unit_tests: config.unit_tests !== false,
      validation_tests: config.validation_tests !== false,
      integration_tests: config.integration_tests === true,
      coverage_target: config.coverage_target || '80',
      max_ai_calls: config.max_ai_calls || 50,
    }
  }
}

/**
 * Get results for a job
 */
async function getResults(apiBaseUrl, jobId, signal) {
  try {
    const response = await fetch(`${apiBaseUrl}/test-generation/results/${jobId}`, { headers: getAuthHeaders(), signal })
    if (response.ok) {
      return await response.json()
    }
  } catch (error) {
    console.warn('Could not retrieve test results:', error.message)
  }
  return null
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, results, testType) {
  return {
    // Job tracking
    jobs: {
      test_generation_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',
    test_type: testType,

    // Paths
    output_path: results?.output_path || data.output_path,

    // Execution metrics
    duration_ms: results?.duration_ms || data.duration_ms,

    // Test info
    framework: results?.framework || data.framework || 'junit5',
    tests_generated: results?.tests_generated || data.tests_generated || 0,
    classes_covered: results?.classes_covered || data.classes_covered || 0,
    methods_covered: results?.methods_covered || data.methods_covered || 0,

    // AI info (smart tests only)
    ai_calls_used: results?.ai_calls_used || data.ai_calls_used || 0,

    // Test files
    tests: results?.tests || [],

    // Generated timestamp
    generated_at: data.created_at || null,

    // Error info if any
    error: data.error || null,
  }
}

// Export executors for both node types
export const testStubsExecutor = {
  execute: executeStubs,
}

export const smartTestsExecutor = {
  execute: executeSmart,
}

export default {
  executeStubs,
  executeSmart,
}
